import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { questionnaireService } from '../services/questionnaireService';
import { fileService } from '../services/fileService';
import { API_ORIGIN } from '../services/publicService';
import { QuestionnaireType } from '../types/questionnaire.types';
import { determineStartingSection, buildAnswersMap } from '../utils/questionnaireFieldUtils';
import { calculateRequiredScores } from '../utils/questionnaireWizardUtils';
import { QuestionnaireWizardBody } from '../components/questionnaires/QuestionnaireWizardBody';

export const PublicQuestionnaireWizardPage: React.FC = () => {
  const params = useParams<{
    tenantId?: string;
    questionnairePath?: string;
    token?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract params - either token-based or path-based URL
  const urlToken = params.token;
  const tenantId = params.tenantId;
  const questionnairePath = params.questionnairePath;

  // All query params from the URL the patient opened (e.g. patient_id);
  // stored on the questionnaire and forwarded to the IL webhook on submit.
  const queryParamsRef = useRef<Record<string, string>>(
    Object.fromEntries(new URLSearchParams(location.search).entries())
  );

  const [questionnaire, setQuestionnaire] = useState<QuestionnaireType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [questionnaireId, setQuestionnaireId] = useState<number | null>(null);
  const [initialAnswers, setInitialAnswers] = useState<Record<string, any>>({});
  const [initialSectionIndex, setInitialSectionIndex] = useState(0);
  const [initialScores, setInitialScores] = useState<Record<string, any>>({});
  const [initialEmailVerifications, setInitialEmailVerifications] = useState<Record<string, { verified: boolean; email: string }>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [clinicState, setClinicState] = useState<string | null>(null);
  // Optional ?referral=<hash>: sent once when the questionnaire is created. All
  // referral behaviour after that reads the referral stored on the questionnaire.
  const referralHashRef = useRef<string | null>(queryParamsRef.current.referral || null);
  const [referralLogoUrl, setReferralLogoUrl] = useState<string | null>(null);
  const [referralId, setReferralId] = useState<number | null>(null);
  // Whether the questionnaire is tied to a lead, and the resolved {variable}
  // values for content substitution — both come from the questionnaire instance.
  const [hasLead, setHasLead] = useState(false);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  // Fallback resolved straight from the link's hash, used only until a
  // questionnaire row exists (a fresh path-based visit has no token yet).
  const [paramReferral, setParamReferral] = useState<{ id: number; logo_url: string | null } | null>(null);

  // Guard against double token creation
  const createTokenRef = useRef(false);

  // Resolve ?referral=<hash> so the header is branded on the very first paint,
  // before any questionnaire row exists. An unknown hash is ignored.
  useEffect(() => {
    const hash = referralHashRef.current;
    if (!hash) return;
    questionnaireService
      .getPublicReferral(hash)
      .then((referral) => setParamReferral({ id: referral.id, logo_url: referral.logo_url }))
      .catch(() => setParamReferral(null));
  }, []);


  useEffect(() => {
    const loadQuestionnaire = async () => {
      try {
        setLoading(true);

        // FLOW 1: Token-based URL (/questionnaire/{token})
        if (urlToken) {
          const questionnaireData = await questionnaireService.getPublicQuestionnaire(urlToken);
          setToken(urlToken);
          setQuestionnaireId(questionnaireData.id);
          setReferralLogoUrl(questionnaireData.referral_logo_url ?? null);
          setReferralId(questionnaireData.referral_id ?? null);
          setHasLead(!!questionnaireData.has_lead);
          setVariableValues(questionnaireData.variable_values ?? {});

          const extractedTenantId = questionnaireData.tenant_id;
          const extractedPath = (questionnaireData as any).questionnaire_type?.path;

          if (!extractedPath) {
            setError('Questionnaire type path not found');
            setLoading(false);
            return;
          }

          const status = questionnaireData.status as string;
          if (status === 'FINISHED') {
            const storageKey = `questionnaire_token_${extractedTenantId}_${extractedPath}`;
            localStorage.removeItem(storageKey);
            setAlreadyCompleted(true);
            setLoading(false);
            return;
          }

          const questionnaireType = await questionnaireService.getPublicQuestionnaireTypeByPath(
            extractedTenantId,
            extractedPath
          );
          setQuestionnaire(questionnaireType);
          setClinicState(questionnaireType.clinic_state ?? questionnaireData.clinic_state ?? null);

          const answersMap = buildAnswersMap(
            questionnaireData.answers || [],
            questionnaireType.definition?.sections || []
          );
          // Prefill unanswered mapped questions from patient data (saved answers win)
          const prefillMap = buildAnswersMap(
            Object.entries(questionnaireData.prefill_answers || {}).map(
              ([question_name, value]) => ({ question_name, answer: [value] })
            ),
            questionnaireType.definition?.sections || []
          );
          setInitialAnswers({ ...prefillMap, ...answersMap });
          setInitialEmailVerifications(questionnaireData.email_verifications || {});

          const sectionIndex = determineStartingSection(
            questionnaireType.definition!,
            answersMap,
            questionnaireData.failed_validation_section_ids || []
          );
          setInitialSectionIndex(sectionIndex);

          setIsSubmitted(['finish', 'SUBMITTED'].includes(status));

          const calculatedScores = await calculateRequiredScores(questionnaireType.definition, urlToken, sectionIndex);
          if (Object.keys(calculatedScores).length > 0) {
            setInitialScores(calculatedScores);
          }
        }
        // FLOW 2: Path-based URL (/questionnaire/{tenantId}/{path})
        else if (tenantId && questionnairePath) {
          const data = await questionnaireService.getPublicQuestionnaireTypeByPath(
            parseInt(tenantId),
            questionnairePath
          );
          setQuestionnaire(data);
          setClinicState(data.clinic_state ?? null);

          const storageKey = `questionnaire_token_${tenantId}_${questionnairePath}`;
          const savedToken = localStorage.getItem(storageKey);

          if (savedToken) {
            try {
              const savedQuestionnaire = await questionnaireService.getPublicQuestionnaire(savedToken);

              const savedStatus = savedQuestionnaire.status as string;
              if (savedStatus === 'FINISHED') {
                localStorage.removeItem(storageKey);
                // Only create_patient questionnaires are one-shot; other types
                // (collect_info, il_webhook) can be filled again — drop the stale
                // token and let the wizard start a fresh questionnaire.
                if (data.type === 'create_patient') {
                  setAlreadyCompleted(true);
                }
                setLoading(false);
                return;
              }

              setToken(savedToken);
              setQuestionnaireId(savedQuestionnaire.id);
              setReferralLogoUrl(savedQuestionnaire.referral_logo_url ?? null);
              setReferralId(savedQuestionnaire.referral_id ?? null);
              setHasLead(!!savedQuestionnaire.has_lead);
              setVariableValues(savedQuestionnaire.variable_values ?? {});

              const savedAnswersMap = buildAnswersMap(
                savedQuestionnaire.answers || [],
                data.definition?.sections || []
              );
              // Prefill unanswered mapped questions from patient data (saved answers win)
              const savedPrefillMap = buildAnswersMap(
                Object.entries(savedQuestionnaire.prefill_answers || {}).map(
                  ([question_name, value]) => ({ question_name, answer: [value] })
                ),
                data.definition?.sections || []
              );
              setInitialAnswers({ ...savedPrefillMap, ...savedAnswersMap });
              setInitialEmailVerifications(savedQuestionnaire.email_verifications || {});

              const sectionIndex = determineStartingSection(
                data.definition!,
                savedAnswersMap,
                savedQuestionnaire.failed_validation_section_ids || []
              );
              setInitialSectionIndex(sectionIndex);

              setIsSubmitted(['finish', 'SUBMITTED'].includes(savedStatus));

              const calculatedScores = await calculateRequiredScores(data.definition, savedToken, sectionIndex);
              if (Object.keys(calculatedScores).length > 0) {
                setInitialScores(calculatedScores);
              }
            } catch (err) {
              localStorage.removeItem(storageKey);
            }
          }
        } else {
          setError('Invalid questionnaire link');
        }
      } catch (err) {
        setError('Questionnaire not found. Please check the link and try again.');
      } finally {
        setLoading(false);
      }
    };

    loadQuestionnaire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToken, tenantId, questionnairePath]);

  // ── ensureToken ───────────────────────────────────────────────────────────

  const ensureToken = useCallback(async (): Promise<string | null> => {
    if (token) return token;
    if (createTokenRef.current) return null; // already creating
    createTokenRef.current = true;
    try {
      if (!questionnaire || !tenantId) return null;
      const response = await questionnaireService.createPublicQuestionnaire(
        parseInt(tenantId),
        questionnaire.id,
        queryParamsRef.current,
        referralHashRef.current
      );
      const newToken = response.token;
      const data = await questionnaireService.getPublicQuestionnaire(newToken);
      setToken(newToken);
      setQuestionnaireId(data.id);
      setReferralLogoUrl(data.referral_logo_url ?? null);
      setReferralId(data.referral_id ?? null);
      setHasLead(!!data.has_lead);
      setVariableValues(data.variable_values ?? {});
      const storageKey = `questionnaire_token_${tenantId}_${questionnairePath}`;
      localStorage.setItem(storageKey, newToken);
      return newToken;
    } finally {
      createTokenRef.current = false;
    }
  }, [token, questionnaire, tenantId, questionnairePath]);

  // ── API adapters ──────────────────────────────────────────────────────────

  const saveAnswers = useCallback(
    (tok: string, answers: any[], lastSection?: { title: string; index: number }) =>
      questionnaireService.savePublicAnswers(tok, answers, lastSection),
    []
  );

  const uploadFileAdapter = useCallback(
    (tok: string, file: globalThis.File, fieldName: string) =>
      fileService.uploadPublicFile(tok, file, fieldName),
    []
  );

  const calculateScoreApi = useCallback(
    (tok: string, scoreName: string) => questionnaireService.calculateScore(tok, scoreName),
    []
  );

  const sendEmailCode = useCallback(
    (tok: string, fieldName: string, email: string) =>
      questionnaireService.sendEmailVerificationCode(tok, fieldName, email),
    []
  );

  const verifyEmailCode = useCallback(
    (tok: string, fieldName: string, code: string) =>
      questionnaireService.verifyEmailCode(tok, fieldName, code),
    []
  );

  const hasPaymentAfterSubmit = (() => {
    const sections = questionnaire?.definition?.sections ?? [];
    const submitSectionIndex = sections.findIndex((s) =>
      s.fields?.some((f) => f.type === 'button' && f.action === 'submit')
    );
    if (submitSectionIndex === -1) return false;
    return sections.slice(submitSectionIndex + 1).some((s) =>
      s.fields?.some((f) => f.type === 'stripe_cc')
    );
  })();

  const submitQuestionnaire = useCallback(
    (tok: string) =>
      questionnaire?.type === 'collect_info' || !hasPaymentAfterSubmit
        ? questionnaireService.finishPublicQuestionnaire(tok)
        : questionnaireService.submitPublicQuestionnaire(tok),
    [questionnaire?.type, hasPaymentAfterSubmit]
  );

  const onExitConfirmed = useCallback(
    async (tok: string | null, exitLocation?: string) => {
      if (tok) {
        await questionnaireService.exitPublicQuestionnaire(tok, exitLocation);
        if (tenantId && questionnairePath) {
          const storageKey = `questionnaire_token_${tenantId}_${questionnairePath}`;
          localStorage.removeItem(storageKey);
        }
      }
    },
    [tenantId, questionnairePath]
  );

  const onExitNavigate = useCallback(
    (exitUrl?: string) => {
      window.location.href = exitUrl ?? '/questionnaire/exited';
    },
    []
  );

  const onComplete = useCallback(() => {
    navigate('/questionnaire/exited');
  }, [navigate]);

  // ── Loading / error guards ────────────────────────────────────────────────

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#E5F0F1',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#3A7380' }} />
      </Box>
    );
  }

  if (alreadyCompleted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#E5F0F1',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <Alert severity="info" sx={{ maxWidth: 480 }}>
          Questionnaire already completed
        </Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#E5F0F1',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Once a questionnaire row exists it is the only source of truth for the referral —
  // including when it has none. The link's ?referral= hash is used solely before that,
  // so a fresh visit is branded from the first paint.
  const hasQuestionnaireRow = questionnaireId !== null;
  const effectiveReferralId = hasQuestionnaireRow ? referralId : paramReferral?.id ?? null;
  const effectiveReferralLogoUrl = hasQuestionnaireRow
    ? referralLogoUrl
    : paramReferral?.logo_url ?? null;

  if (!questionnaire || !questionnaire.definition) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#E5F0F1',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Alert severity="warning">No questionnaire data available.</Alert>
      </Box>
    );
  }

  return (
    <QuestionnaireWizardBody
      definition={questionnaire.definition}
      questionnaireId={questionnaireId}
      tenantId={questionnaire.tenant_id}
      initialToken={token}
      initialAnswers={initialAnswers}
      initialSectionIndex={initialSectionIndex}
      initialScores={initialScores}
      initialEmailVerifications={initialEmailVerifications}
      ensureToken={ensureToken}
      saveAnswers={saveAnswers}
      uploadFile={uploadFileAdapter}
      calculateScoreApi={calculateScoreApi}
      submitQuestionnaire={submitQuestionnaire}
      sendEmailCode={sendEmailCode}
      verifyEmailCode={verifyEmailCode}
      onExitConfirmed={onExitConfirmed}
      onExitNavigate={onExitNavigate}
      onComplete={onComplete}
      exitPopupVariant="questionnaire"
      isSubmitted={isSubmitted}
      language={questionnaire.language}
      clinicState={clinicState}
      logoSrc={
        questionnaire.type === 'il_webhook' && questionnaire.logo_url
          ? `${API_ORIGIN}${questionnaire.logo_url}`
          : undefined
      }
      rightLogoSrc={effectiveReferralLogoUrl ? `${API_ORIGIN}${effectiveReferralLogoUrl}` : undefined}
      referralId={effectiveReferralId}
      hasLead={hasLead}
      variableValues={variableValues}
    />
  );
};
